import { getMenuRoutesForPortal, resolveMenuItemPath } from '@/router/portalRoutes.js'
import { getPortalForCurrentRoute } from '@/utils/authSession.js'
import { t } from '@/locale/index.js'

/** 独立详情页（不在子菜单 children 中） */
const DETAIL_ROUTE_MAP = {
  '/machinist-detail': {
    parentTitle: '机手管理',
    parentPath: '/Machinist',
    title: () => t('detail.machinist'),
  },
  '/captain-detail': {
    parentTitle: '队长管理',
    parentPath: '/Captain',
    title: () => t('detail.captain'),
  },
  '/equipment/device-detail': {
    parentTitle: '设备管理',
    parentPath: '/equipment/device',
    title: () => t('detail.device'),
  },
}

export function normalizePath(path) {
  if (!path) return ''
  const text = String(path)
  if (text.length > 1 && text.endsWith('/')) {
    return text.slice(0, -1)
  }
  return text
}

export function isPathMatch(currentPath, menuPath) {
  const path = normalizePath(currentPath)
  const target = normalizePath(menuPath)
  if (!target || target === '/') return path === target
  if (path === target) return true
  return path.startsWith(`${target}/`)
}

/** 左侧主导航：当前路由是否属于该菜单（含子菜单与详情页） */
export function isSidebarMenuActive(item, currentPath, portal = getPortalForCurrentRoute()) {
  const path = normalizePath(currentPath)
  const menuPath = normalizePath(resolveMenuItemPath(item, portal))

  if (!item.children?.length) {
    if (path === menuPath) return true
    const detail = DETAIL_ROUTE_MAP[path]
    return Boolean(detail && normalizePath(detail.parentPath) === menuPath)
  }

  if (isPathMatch(path, menuPath)) return true

  if (item.children.some((child) => normalizePath(child.path) === path)) {
    return true
  }

  const detail = DETAIL_ROUTE_MAP[path]
  if (detail) {
    return item.children.some(
      (child) => normalizePath(child.path) === normalizePath(detail.parentPath),
    )
  }

  return false
}

function findMenuChild(routes, path) {
  const currentPath = normalizePath(path)

  for (const route of routes) {
    if (!route.children?.length) continue

    const exact = route.children.find((child) => normalizePath(child.path) === currentPath)
    if (exact) {
      return { parent: route, child: exact }
    }

    const prefixChild = [...route.children]
      .filter((child) => isPathMatch(currentPath, child.path) && normalizePath(child.path) !== currentPath)
      .sort((a, b) => normalizePath(b.path).length - normalizePath(a.path).length)[0]

    if (prefixChild) {
      return { parent: route, child: prefixChild }
    }
  }

  for (const route of routes) {
    if (normalizePath(route.path) === currentPath && route.title) {
      return { parent: null, child: route }
    }
  }

  return null
}

function findParentChild(routes, path) {
  return findMenuChild(routes, path)
}

function dedupeBreadcrumbs(crumbs) {
  if (crumbs.length < 2) return crumbs
  if (crumbs[0].title === crumbs[1].title) {
    return [crumbs[1]]
  }
  return crumbs
}

export function getBreadcrumbsByPath(path, portal = getPortalForCurrentRoute()) {
  if (!path || path === '/home') {
    return dedupeBreadcrumbs([
      { title: t('home'), path: '/home' },
      { title: t('workbench'), path: '/home' },
    ])
  }

  const detailMeta = DETAIL_ROUTE_MAP[path]
  if (detailMeta) {
    return dedupeBreadcrumbs([
      { title: detailMeta.parentTitle, path: detailMeta.parentPath },
      { title: detailMeta.title(), path },
    ])
  }

  const routes = getMenuRoutesForPortal(portal)
  const match = findParentChild(routes, path)

  if (match?.parent && match.child) {
    return dedupeBreadcrumbs([
      { title: match.parent.title, path: resolveMenuItemPath(match.parent) },
      { title: match.child.title, path: match.child.path },
    ])
  }
  if (match?.child) {
    return [{ title: match.child.title, path: match.child.path }]
  }

  const title = getPageTitleByPath(path, portal)
  return title ? [{ title, path }] : []
}

function matchRouteTitle(routes, path) {
  const detailMeta = DETAIL_ROUTE_MAP[path]
  if (detailMeta) return detailMeta.title()

  const match = findMenuChild(routes, path)
  if (match?.child?.title) {
    return match.child.title
  }

  return ''
}

export function getPageTitleByPath(path, portal = getPortalForCurrentRoute()) {
  if (!path || path === '/home') {
    return t('workbench')
  }
  return matchRouteTitle(getMenuRoutesForPortal(portal), path)
}

export function isDetailPage(path) {
  return Boolean(DETAIL_ROUTE_MAP[path])
}

export function getParentMenuTitle(path, portal = getPortalForCurrentRoute()) {
  const crumbs = getBreadcrumbsByPath(path, portal)
  if (crumbs.length >= 2) return crumbs[0].title
  return ''
}
